import React, { useState, useEffect, useMemo } from 'react';
import { AlertCircle, Activity, Mail, Calendar, Trophy, DollarSign, Users, MessageSquareReply, ThumbsUp } from 'lucide-react';
import { useEmailAgentLeads, useEmailMessageLogs, useMeetingsBooked } from '../../hooks/useSupabaseLeads';
import { usePipelineDeals } from '../../hooks/usePipelineDeals';
import AgentHeader from '../CallAgent/AgentHeader';
import AgentActionPanel from '../CallAgent/AgentActionPanel';
import TodayHighlights from '../CallAgent/TodayHighlights';
import ActivityFeed from '../CallAgent/ActivityFeed';
import PerformanceIntelligence from '../shared/PerformanceIntelligence';
import AgentMilestones from '../shared/AgentMilestones';
import type { MilestoneTier } from '../shared/AgentMilestones';
import NextActionsPanel from '../CallAgent/NextActionsPanel';
import TestAgentModal from '../CallAgent/TestAgentModal';
import EmailHowIWorkModal from './EmailHowIWorkModal';
import FeedbackPopup from '../FeedbackPopup';
import LoadingScreen from '../shared/LoadingScreen';
import { getAgentSettingsRow } from '../../services/settingsService';
import { useTasksQueue } from '../../hooks/useTasksQueue';

const EmailAgentPage = () => {
  const { data: allEmailLeads, isLoading, error } = useEmailAgentLeads();
  const taskQueue = useTasksQueue('emailagent');
  const { data: pipelineDeals } = usePipelineDeals();
  const { data: emailMessageLogs } = useEmailMessageLogs();
  const { data: allMeetings } = useMeetingsBooked();
  const [showTestAgentModal, setShowTestAgentModal] = useState(false);
  const [showHowIWorkModal, setShowHowIWorkModal] = useState(false);
  const [agentAvatarUrl, setAgentAvatarUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    getAgentSettingsRow('email').then(row => {
      if (row?.avatar_url) setAgentAvatarUrl(row.avatar_url);
    });
  }, []);

  const allEmailLogs = useMemo(() => {
    return [...allEmailLeads].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [allEmailLeads]);

  const todayStart = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const todayEnd = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  }, []);

  const todayEmailLogs = useMemo(() => {
    return allEmailLogs.filter(log => {
      const logDate = new Date(log.created_at);
      return logDate >= todayStart && logDate <= todayEnd;
    });
  }, [allEmailLogs, todayStart, todayEnd]);

  const recentEmailLogs = useMemo(() => {
    return allEmailLogs.slice(0, 50);
  }, [allEmailLogs]);

  const todayEmLogs = useMemo(() => {
    return emailMessageLogs.filter(log => {
      const d = new Date(log.created_at);
      return d >= todayStart && d <= todayEnd;
    });
  }, [emailMessageLogs, todayStart, todayEnd]);

  const todayStats = useMemo(() => {
    const sentLogs = todayEmLogs.filter(l => l.from_role === 'user');
    const leadsContacted = new Set(sentLogs.map(l => l.lead_id).filter(Boolean)).size;
    const emailsSent = sentLogs.length;
    const repliesReceived = todayEmLogs.filter(l => (l.from_role === 'lead' || l.from_role === 'prospect') && l.status === 'received').length;

    const repliedLeadIds = new Set(todayEmLogs.filter(l => (l.from_role === 'lead' || l.from_role === 'prospect') && l.status === 'received').map(l => l.lead_id).filter(Boolean));
    const positiveConversations = allEmailLeads.filter(lead => {
      if (!repliedLeadIds.has(lead.lead_id)) return false;
      const temp = (lead.ea_lead_temp || '').toLowerCase();
      return temp === 'warm' || temp === 'hot';
    }).length;

    const todayMeetings = allMeetings.filter(m => {
      const d = new Date(m.created_at);
      const channel = (m.booked_channel || '').toLowerCase();
      return d >= todayStart && d <= todayEnd && channel.includes('email');
    }).length;

    return { leadsContacted, emailsSent, repliesReceived, positiveConversations, meetingsBooked: todayMeetings };
  }, [todayEmLogs, allEmailLeads, allMeetings, todayStart, todayEnd]);

  const agentStatus = useMemo(() => {
    if (allEmailLogs.length === 0) return 'idle';

    const lastEmail = allEmailLogs[0];
    const lastEmailTime = new Date(lastEmail.created_at);
    const now = new Date();
    const hoursSinceLastEmail = (now.getTime() - lastEmailTime.getTime()) / (1000 * 60 * 60);

    if (hoursSinceLastEmail < 1) return 'active';
    if (allEmailLeads.some(lead => lead.ea_next_fu_datetime && new Date(lead.ea_next_fu_datetime) > now)) return 'idle';
    return 'processing';
  }, [allEmailLogs, allEmailLeads]);

  const meetingsBooked = useMemo(() => {
    return allMeetings.filter(m => {
      const channel = (m.booked_channel || '').toLowerCase();
      return channel.includes('email');
    }).length;
  }, [allMeetings]);

  const pipelineValue = useMemo(() => {
    return pipelineDeals
      .filter(d => (d.channel_booked || '').toLowerCase().includes('email'))
      .reduce((sum, d) => sum + (d.deal_value || 0), 0);
  }, [pipelineDeals]);

  const milestoneTiers: MilestoneTier[] = useMemo(() => [
    {
      icon: Trophy,
      title: '1,000 Emails Sent',
      target: 1000,
      reward: 'Get 200 Free Email Credits',
      currentValue: emailMessageLogs.filter(l => l.from_role === 'user').length,
      formatValue: (c, t) => `${c.toLocaleString()} / ${t.toLocaleString()} emails`,
    },
    {
      icon: Calendar,
      title: '10 Meetings Booked',
      target: 10,
      reward: '500 Free Email Credits',
      currentValue: meetingsBooked,
      formatValue: (c, t) => `${c} / ${t} meetings booked`,
    },
    {
      icon: DollarSign,
      title: '$150,000 Pipeline Generated',
      target: 150000,
      reward: '1,000 Free Email Credits',
      currentValue: pipelineValue,
      formatValue: (c, t) => `$${c.toLocaleString()} / $${t.toLocaleString()}`,
    },
  ], [emailMessageLogs, meetingsBooked, pipelineValue]);


  if (isLoading && allEmailLeads.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg flex items-center space-x-2 mb-6">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      <AgentHeader
        status={agentStatus}
        title="Email Agent"
        subtitle="I write personalized emails, send email campaigns and follow up until meetings get booked."
        icon="mail"
        avatarSrc={agentAvatarUrl}
        avatarAlt="Email Agent"
        activeLabel="Active — Sending Emails"
        processingMessages={[
          'Preparing campaign insights…',
          'Analyzing reply sentiment…',
          'Optimizing subject lines…',
          'Crafting personalized emails…',
          'Monitoring open rates…',
          'Preparing follow-ups…'
        ]}
      />

      <AgentActionPanel
        onTestCall={() => setShowTestAgentModal(true)}
        onHowIWork={() => setShowHowIWorkModal(true)}
        campaignPath="/campaigns/email"
        settingsPath="email-agent"
        howIWorkDescription="My email writing, sending, and follow-up process."
        viewCampaignDescription="See the email sequence steps and campaign performance."
        testMeDescription="Have a full email conversation with me live."
        testMeIcon={Mail}
        customizeMeDescription="Adjust identity, templates, and email behavior rules."
      />

      <div className="mt-[60px]">
        <TodayHighlights
          metrics={[
            { value: todayStats.leadsContacted, label: 'Leads Contacted', icon: Users },
            { value: todayStats.emailsSent, label: 'Emails Sent', icon: Mail },
            { value: todayStats.repliesReceived, label: 'Replies Received', icon: MessageSquareReply },
            { value: todayStats.positiveConversations, label: 'Positive Conversations', icon: ThumbsUp },
            { value: todayStats.meetingsBooked, label: 'Meetings Booked', icon: Calendar },
          ]}
        />
      </div>

      <div className="space-y-3 mt-[60px]">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-lg">
            <Activity className="text-white" size={22} />
          </div>
          <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">Live Operations</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ minHeight: '600px' }}>
          <ActivityFeed
            processingTasks={taskQueue.processingTasks}
            recentFinishedTasks={taskQueue.recentFinishedTasks}
            hasProcessing={taskQueue.hasProcessing}
            hasAnyTasks={taskQueue.hasAnyTasks}
            isLoading={taskQueue.isLoading}
            emptyState={{ icon: Mail, title: 'No emails in queue', subtitle: 'Assign leads to start emailing' }}
          />
          <NextActionsPanel
            pendingTasks={taskQueue.pendingTasks}
            hasAnyTasks={taskQueue.hasAnyTasks}
            isLoading={taskQueue.isLoading}
            emptyState={{ icon: Mail, title: 'No emails scheduled', subtitle: 'Follow-ups and new emails will appear here' }}
          />
        </div>
      </div>

      <div className="mt-[60px]">
        <PerformanceIntelligence agentType="email" />
      </div>

      <div className="mt-[60px]">
        <AgentMilestones tiers={milestoneTiers} />
      </div>

      <TestAgentModal
        isOpen={showTestAgentModal}
        onClose={() => setShowTestAgentModal(false)}
        agentType="email"
      />

      <EmailHowIWorkModal
        isOpen={showHowIWorkModal}
        onClose={() => setShowHowIWorkModal(false)}
      />

      <FeedbackPopup isVisible={true} />
    </div>
  );
};

export default EmailAgentPage;
